import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { useMassagerAuthContext } from '@shared/providers/MassagerAuthContext';
import {
  useMassagerResetPassword,
  useMassagerValidateResetToken,
} from '@/hooks/massager/useMassagerAuth';
import { useTenantSiteCheck } from '@/hooks/admin/useTenantSiteCheck';
import {
  massagerResetPasswordFormSchema,
  type MassagerResetPasswordFormData,
} from '@/schemas/massagerResetPasswordSchema';
import { AppAdminFormLabel, AppButton, AppForm, AppTextField } from '@/components/atoms';
import useAppToast from '@/hooks/useAppToast';

const MAX_INPUT_LENGTH = 255;

/** Enforce maxLength on Ant Design Input (which only shows a counter by default). */
const enforceMaxLength = (
  e: React.ChangeEvent<HTMLInputElement>,
  fieldOnChange: (...args: unknown[]) => void,
) => {
  const value = e.target.value.length > MAX_INPUT_LENGTH
    ? e.target.value.slice(0, MAX_INPUT_LENGTH)
    : e.target.value;
  fieldOnChange(value);
};

const RESET_SUCCESS_TOAST = 'パスワードがリセットされました。';
const TOAST_ERROR_MESSAGE = 'エラーが発生しました。';
const VALIDATION_TOAST_KEY = 'massager-reset-validation-error';

const NO_MATCHING_USER_MESSAGE = '治療院ID、施術者ログインIDが誤っている可能性があります。';

const isNoMatchingUserMessage = (message?: string | null): boolean => {
  if (!message) return false;
  return message.includes('治療院IDが見つかりません') || message.includes('ユーザIDが見つかりません');
};

/**
 * Treat any token-related failure (60min expiry OR already-used / deleted /
 * forged token) as "expired link" — both cases show the user the same
 * /reset-expired screen so they can request a fresh email.
 *
 * Backend messages this matches:
 *  - 「トークンの有効期限が切れています。」     (60min expired)
 *  - 「パスワードリセットのリクエストが見つかりません。」(token row deleted, e.g. already reset)
 *  - 「トークンが無効です。」 / 「無効なトークンです。」 (hash mismatch / forged)
 */
const isInvalidOrExpiredTokenMessage = (message?: string | null): boolean => {
  if (!message) return false;
  return (
    (message.includes('有効期限') && message.includes('切れ')) ||
    message.includes('パスワードリセットのリクエストが見つかりません') ||
    message.includes('トークンが無効') ||
    message.includes('無効なトークン')
  );
};

/**
 * Massager (checkin) reset password page.
 *
 * Reached via the link in the password-reset email:
 *   /{site}/massager/password/reset/:token
 *
 * Legacy email links may still include clinic_number/login_id query params;
 * those are used only for prefill and early token validation.
 */
const MassagerResetPasswordPage: React.FC = () => {
  const { site, token } = useParams<{ site: string; token: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { login: massagerLogin } = useMassagerAuthContext();
  const toast = useAppToast();

  const {
    data: siteCheck,
    isLoading: isCheckingSite,
    isError: isSiteCheckError,
  } = useTenantSiteCheck(site);

  const validSite =
    !!site &&
    !isCheckingSite &&
    !isSiteCheckError &&
    !!siteCheck?.success &&
    !!siteCheck.data?.exists;

  const validateResetTokenMutation = useMassagerValidateResetToken();
  const resetPasswordMutation = useMassagerResetPassword();

  const [isValidating, setIsValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const isLoading = resetPasswordMutation.isPending;

  const clinicNumberFromUrl = searchParams.get('clinic_number') || '';
  const loginIdFromUrl = searchParams.get('login_id') || '';

  const {
    control,
    handleSubmit,
    setValue,
    setError: setFormError,
    clearErrors,
  } = useTrimmedForm<MassagerResetPasswordFormData>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(massagerResetPasswordFormSchema),
    defaultValues: {
      clinic_number: clinicNumberFromUrl,
      login_id: loginIdFromUrl,
      password: '',
      password_confirmation: '',
    },
  });

  useEffect(() => {
    // Wait for site validation to complete; skip if site is invalid so we
    // don't fire the validate-token API against a bogus site_type.
    if (isCheckingSite) return;
    if (!validSite) return;

    const validateToken = async () => {
      if (!token) {
        navigate(`/${site || 'medix'}/massager/password/reset-expired`, { replace: true });
        return;
      }

      try {
        const response = await validateResetTokenMutation.mutateAsync({
          site: site || 'medix',
          token,
          clinicNumber: clinicNumberFromUrl,
          loginId: loginIdFromUrl,
        });

        if (response.success && response.data?.valid) {
          setTokenValid(true);
          if (clinicNumberFromUrl) {
            setValue('clinic_number', clinicNumberFromUrl);
          }
          if (loginIdFromUrl) {
            setValue('login_id', loginIdFromUrl);
          }
        } else {
          navigate(`/${site || 'medix'}/massager/password/reset-expired`, { replace: true });
        }
      } catch {
        navigate(`/${site || 'medix'}/massager/password/reset-expired`, { replace: true });
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Re-run once the site_type validation finishes (validSite flips true)
    // so we don't skip the token check after a brief loading period.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, clinicNumberFromUrl, loginIdFromUrl, isCheckingSite, validSite]);

  const handleResetError = (message: string) => {
    if (isInvalidOrExpiredTokenMessage(message)) {
      navigate(`/${site || 'medix'}/massager/password/reset-expired`, { replace: true });
      return;
    }

    // Generic toast at top for any failure.
    toast.error(TOAST_ERROR_MESSAGE);

    if (isNoMatchingUserMessage(message)) {
      // Clinic / login_id mismatch — under 施術者ログインID field per spec.
      setFormError('login_id', { type: 'server', message: NO_MATCHING_USER_MESSAGE });
    } else {
      // Unrecognized server error — fall back to a form-level alert so the
      // user still sees the underlying reason (toast alone is too generic).
      setError(message);
    }
  };

  const onSubmit = async (data: MassagerResetPasswordFormData) => {
    if (!token) return;
    setError(null);
    clearErrors();

    try {
      const response = await resetPasswordMutation.mutateAsync({
        site: site || 'medix',
        token,
        clinicNumber: data.clinic_number,
        loginId: data.login_id,
        password: data.password,
        passwordConfirmation: data.password_confirmation,
      });

      if (response.success) {
        // Auto-login with the new password so the user lands on /home
        // already authenticated. If auto-login fails (rare — credentials
        // were just verified), fall back to showing an inline success
        // message so they can navigate to the login page manually.
        const loginResult = await massagerLogin({
          clinic_number: data.clinic_number,
          login_id: data.login_id,
          password: data.password,
          site_type: site,
        });

        if (loginResult.success) {
          toast.success(RESET_SUCCESS_TOAST);
          navigate(`/${site || 'medix'}/massager/home`, { replace: true });
          return;
        }

        setSuccess(response.message || 'パスワードを変更しました。');
        toast.error(TOAST_ERROR_MESSAGE);
        setError(loginResult.message || 'ログインに失敗しました。');
        return;
      }

      handleResetError(response.message || 'エラーが発生しました。');
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        handleResetError(axiosError.response?.data?.message || 'エラーが発生しました。');
      } else {
        toast.error(TOAST_ERROR_MESSAGE);
        setError('エラーが発生しました。');
      }
    }
  };

  const onInvalid = () => {
    toast.error(TOAST_ERROR_MESSAGE, { key: VALIDATION_TOAST_KEY });
  };

  // Invalid site_type guard — same UX as LoginPage / ForgotPasswordPage.
  if (site && !isCheckingSite && (!validSite || isSiteCheckError)) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center overflow-x-hidden bg-[#fa7c64] px-4 py-8">
        <div className="w-full max-w-[420px] rounded-lg bg-white px-6 py-6 text-center shadow-lg">
          <h1 className="mb-3 text-lg font-bold text-red-600">無効なサイト</h1>
          <p className="mb-4 text-sm text-gray-600">指定されたサイトは存在しません。</p>
          <AppButton
            type="primary"
            onClick={() => navigate('/medix/massager/login')}
            style={{ backgroundColor: colors.primary }}
            className="w-full sm:w-auto"
            data-testid="massager-reset-invalid-site-back"
          >
            ログイン画面へ
          </AppButton>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col overflow-x-hidden bg-[#fa7c64]">
      <div className="flex flex-1 flex-col items-center justify-center px-4 py-6 sm:py-10">
        <div className="w-full max-w-[420px]">
          <div className="rounded-lg bg-white px-5 py-6 shadow-lg sm:px-8">
            <h1 className="mb-4 text-center text-base font-bold text-gray-700 sm:text-lg">
              パスワードリセット
            </h1>

            {isValidating && (
              <div className="py-4 text-center">
                <p className="text-gray-600">トークンを確認中...</p>
              </div>
            )}

            {success && (
              <div
                role="status"
                className="mb-4 rounded border border-green-600 bg-green-50 px-3 py-2.5 text-left text-sm leading-relaxed text-green-900"
                data-testid="massager-reset-success-message"
              >
                {success}
              </div>
            )}

            {error && !isValidating && (
              <div
                role="alert"
                className="mb-4 rounded border border-red-600 bg-red-50 px-3 py-2.5 text-left text-sm leading-relaxed text-red-900"
                data-testid="massager-reset-error-message"
              >
                {error}
              </div>
            )}

            {!isValidating && tokenValid && !success && (
              <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-3">
                <Controller
                  name="clinic_number"
                  control={control}
                  render={({ field, fieldState }) => (
                    <AppForm.Item
                      validateStatus={fieldState.error ? 'error' : undefined}
                      help={fieldState.error?.message}
                      className="mb-0"
                    >
                      <AppAdminFormLabel label="治療院ID" className="mb-1" />
                      <AppTextField
                        {...field}
                        onChange={(e) => enforceMaxLength(e, field.onChange)}
                        id="clinic_number"
                        className="w-full"
                        autoComplete="username"
                        maxLength={MAX_INPUT_LENGTH}
                        data-testid="massager-reset-clinic-number-input"
                        size="large"
                      />
                    </AppForm.Item>
                  )}
                />

                <Controller
                  name="login_id"
                  control={control}
                  render={({ field, fieldState }) => (
                    <AppForm.Item
                      validateStatus={fieldState.error ? 'error' : undefined}
                      help={fieldState.error?.message}
                      className="mb-0"
                    >
                      <AppAdminFormLabel label="施術者ログインID" className="mb-1" />
                      <AppTextField
                        {...field}
                        onChange={(e) => enforceMaxLength(e, field.onChange)}
                        id="login_id"
                        className="w-full"
                        autoComplete="username"
                        maxLength={MAX_INPUT_LENGTH}
                        data-testid="massager-reset-login-id-input"
                        size="large"
                      />
                    </AppForm.Item>
                  )}
                />

                <Controller
                  name="password"
                  control={control}
                  render={({ field, fieldState }) => (
                    <AppForm.Item
                      validateStatus={fieldState.error ? 'error' : undefined}
                      help={fieldState.error?.message}
                      className="mb-0"
                    >
                      <AppAdminFormLabel label="新しいパスワード" className="mb-1" />
                      <AppTextField
                        {...field}
                        onChange={(e) => enforceMaxLength(e, field.onChange)}
                        id="password"
                        type="text"
                        className="w-full"
                        autoComplete="new-password"
                        maxLength={MAX_INPUT_LENGTH}
                        data-testid="massager-reset-password-input"
                        size="large"
                      />
                    </AppForm.Item>
                  )}
                />

                <Controller
                  name="password_confirmation"
                  control={control}
                  render={({ field, fieldState }) => (
                    <AppForm.Item
                      validateStatus={fieldState.error ? 'error' : undefined}
                      help={fieldState.error?.message}
                      className="mb-0"
                    >
                      <AppAdminFormLabel label="新しいパスワード確認用" className="mb-1" />
                      <AppTextField
                        {...field}
                        onChange={(e) => enforceMaxLength(e, field.onChange)}
                        id="password_confirmation"
                        type="password"
                        className="w-full"
                        autoComplete="new-password"
                        maxLength={MAX_INPUT_LENGTH}
                        data-testid="massager-reset-password-confirmation-input"
                        size="large"
                      />
                    </AppForm.Item>
                  )}
                />

                <div className="pt-2">
                  <AppButton
                    type="primary"
                    htmlType="submit"
                    size="large"
                    loading={isLoading}
                    disabled={isLoading}
                    className="w-full transition-opacity duration-200 hover:opacity-90"
                    style={{ backgroundColor: colors.primary }}
                    data-testid="massager-reset-submit"
                  >
                    {isLoading ? '設定中...' : 'パスワードを設定する'}
                  </AppButton>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MassagerResetPasswordPage;
